import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useContext, useEffect, useState } from "react";
import { FiPlus } from "react-icons/fi";
import { DialogProvider, createDialogContext } from "src/Common/Dialog";
import { KYDMultiSelect } from "src/ui";
import actions from "src/actions";

export const EditTagsDialogContext = createDialogContext();

const EditTagsDialog = ({
  onSave,
  onClose,
  initialValue = [],
}: {
  onClose: () => void;
  initialValue?: string[];
  onSave: (data: string[]) => Promise<void>;
}) => {
  const [loading, setLoading] = useState(false);
  const { isOpen } = useContext(EditTagsDialogContext);
  const [value, setValue] = useState<string[]>(initialValue || []);
  const [tags, setTags] = useState<{ label: string; value: string }[]>([]);

  const fetchOrganizationTags = useCallback(async () => {
    const res = await actions.ADMIN.fetchOrganizationTags();
    setTags(res.map((tag) => ({ label: tag, value: tag })));
  }, []);

  const handleSave = async () => {
    setLoading(true);
    await onSave(value);
    setValue([]);
    setLoading(false);
  };

  useEffect(() => {
    if (!isOpen) {
      setValue([]);
    }

    if (isOpen) {
      fetchOrganizationTags();
      setValue(initialValue);
    }
  }, [isOpen, fetchOrganizationTags]);

  return (
    <Modal
      closeOnOverlayClick={false}
      isOpen={isOpen}
      onClose={onClose}
      isCentered
    >
      <ModalOverlay />
      <ModalContent
        mb={[0, "auto"]}
        borderBottomLeftRadius={[0, "md"]}
        borderBottomRightRadius={[0, "md"]}
      >
        <ModalHeader paddingTop={4}>
          <VStack gap={0} width={"full"}>
            <Text w="full">Edit tags</Text>
          </VStack>
        </ModalHeader>
        <ModalBody textAlign={"left"}>
          <KYDMultiSelect
            icon={FiPlus}
            createable
            label="Tags"
            placeholder="Search tags"
            options={tags}
            values={value.map((v) => ({
              label: v,
              value: v,
            }))}
            onChange={(vals) => {
              setValue(vals.map((v) => v.value));
            }}
          />
        </ModalBody>
        <ModalFooter>
          <Stack direction={["column-reverse", "row-reverse"]} width="100%">
            <Button key={"cancel"} w={"auto"} onClick={onClose}>
              Cancel
            </Button>
            <Button
              key={"submit"}
              variant="kydDark"
              w={"auto"}
              onClick={handleSave}
              isLoading={loading}
              isDisabled={loading || !value}
            >
              Save
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const withEditTagsDialog =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => {
    return (
      <DialogProvider context={EditTagsDialogContext}>
        <Component {...(props as T)} />
      </DialogProvider>
    );
  };

export default EditTagsDialog;
